import { onMounted, ref } from 'vue';
import { provideLocal, injectLocal } from '@vueuse/core';
import { useUrlLocationOperations } from '~/composables/useUrlLocationOperations';
import { useEnvProps } from '~/composables/useEnvProps';
import buildUrl from '~/utils/buildUrl';
import { OSVersionSupported, urlContainsMarxentCTATrue } from '~/utils/OSVersionSupportedUtil';
import { loadScript } from '~/utils/loadScript';

// Loads Marxent script asynchronously

/**
 * @type {import('vue').InjectionKey<{
 * isArAssetsAvailable: import('vue').Ref<boolean?>,
 * pregeneratedFunctions: import('vue').Ref<{
 * getWebArPregeneratedUrl: (sku: number | string, lod: number | string) => string;
 * getAssetTypesAvailable: (sku: number | string) => any[]} | null>,
 * marxentUrl: import('vue').Ref<string | null | undefined>,
 * wasScriptLoaded: import('vue').Ref<boolean>
 * }>}
 */
const MARXENT_KEY_REGISTRY = Symbol.for('MARXENT_KEY_REGISTRY');

/**
 * Returns data-testid string to use it in find and findAll
 * @param {string} marxentScriptUrl
 */

const loadMarxentScript = loadScript;

export const useMarxentProvide = () => {
  /** @type {import('vue').Ref<boolean?>} */
  const isArAssetsAvailable = ref(null);
  /**
   * @type {import('vue').Ref<{
   * getWebArPregeneratedUrl: (sku: number | string, lod: number | string) => string;
   * getAssetTypesAvailable: (sku: number | string) => any[]} | null>}
   */
  const pregeneratedFunctions = ref(null);

  /** @type {import('vue').Ref<string | null | undefined>} */
  const marxentUrl = ref('');

  /** @type {import('vue').Ref<boolean>} */
  const wasScriptLoaded = ref(false);

  provideLocal(MARXENT_KEY_REGISTRY, {
    isArAssetsAvailable,
    pregeneratedFunctions,
    marxentUrl,
    wasScriptLoaded,
  });
};

function useMarxent() {
  const { isProduction } = useEnvProps();
  const { getUrlData } = useUrlLocationOperations();
  const injected = injectLocal(MARXENT_KEY_REGISTRY);
  if (!injected) {
    throw new Error('useMarxent() is called without providing the Marxent context with useMarxentProvide()');
  }
  const { isArAssetsAvailable, pregeneratedFunctions, marxentUrl, wasScriptLoaded } = injected;

  /**
   * Get the QR URL for a specific SKU and API credentials.
   * @param {{productUrl: string, sku: string, extras: Record<string, string>}}
   * @returns {string} generated QR URL.
   */

  const getQrUrl = ({ productUrl, sku, extras }) => {
    const lod = '1024'; // level of detail
    const url = getUrlData();

    const { origin } = url;

    /** Insert the swatch change code here */

    const params = {
      marxentCTA: 'true',
      sku,
      lod,
    };

    const qrUrl = buildUrl(origin, productUrl, { ...params, ...extras });

    return qrUrl;
  };

  /**
   * Sets the QR code on the given QR Element
   * @param {{productUrl: string, qrElement: any, sku: string, extras: Record<string, string>}} producturl
   */
  const setQrCode = ({ productUrl, qrElement, sku, extras = {} }) => {
    const qrUrl = getQrUrl({ productUrl, sku, extras });
    new window.QRious({
      element: qrElement,
      size: 300,
      value: qrUrl,
    });
  };
  /**
   * @param {number} sku
   */

  const checkForMarxentAsset = async (sku) => {
    try {
      if (pregeneratedFunctions.value) {
        const arAssetsAvailableArray = await pregeneratedFunctions.value?.getAssetTypesAvailable(sku);
        return arAssetsAvailableArray.length > 0;
      }
      return false;
    } catch (e) {
      console.warn(e);
      return false;
    }
  };

  /**
   * Handles Marxent API credentials, checking for validity and parsing them if necessary.
   *
   * @param {string} marxentApiCredentials - The Marxent API credentials in JSON format (optional).
   * @returns {object | null} parsed credentials object if valid, or null if credentials are missing or invalid.
   */
  const handleMarxentCredentials = (marxentApiCredentials) => {
    if (!marxentApiCredentials) {
      if (isProduction) {
        console.warn('Marxent API credentials are missing or invalid.');
      }
      return null;
    }
    return JSON.parse(marxentApiCredentials);
  };

  /**
   * @param {number} upcNumber
   * @param {string} marxentApiCredentials
   * @returns {Promise<{
   *    marxentUrl: import('vue').Ref<string | null | undefined>,
   *    isArAssetsAvailable: import('vue').Ref<boolean | null | undefined>
   * }>}
   */

  const initMarxent = async (upcNumber, marxentApiCredentials) => {
    // if (OSNotSupported.value) return;
    // TODO: move to package json or env

    const marxentScriptUrl = 'https://cdn.3dcloud.io/mxt-ar-sdk/7.5.0/MxtWebAR-7.5.0.min.js';

    try {
      if (!wasScriptLoaded.value) {
        await loadMarxentScript(marxentScriptUrl);
        const parsedCredentials = handleMarxentCredentials(marxentApiCredentials);
        if (parsedCredentials) {
          pregeneratedFunctions.value = new window.MxtWebAR.MxtWebArPregenerated(parsedCredentials);
        }
        wasScriptLoaded.value = true;
      }
      const lod = '1024'; // level of detail(lod)

      isArAssetsAvailable.value = await checkForMarxentAsset(upcNumber);

      if (isArAssetsAvailable.value) {
        marxentUrl.value = await pregeneratedFunctions.value?.getWebArPregeneratedUrl(upcNumber, lod);
      } else {
        marxentUrl.value = null;
      }
    } catch (e) {
      console.error(e);
    }

    return { marxentUrl, isArAssetsAvailable };
  };

  const showNotSupportedNotification = ref(false);

  const setShowNotSupported = () => {
    if (!OSVersionSupported()) {
      // If OS version is not supported and autostart is true show notification
      return urlContainsMarxentCTATrue();
    }
    // If device is supported we will not show notification
    return false;
  };

  onMounted(() => {
    showNotSupportedNotification.value = setShowNotSupported();
  });

  return {
    initMarxent,
    setQrCode,
    showNotSupportedNotification,
    checkForMarxentAsset,
  };
}

export default useMarxent;
